// InvoiceRowDetail — the expanded content for one invoice row in the review
// queue. Presents the in-context derivation the backend attached (`derived`):
// a SharePoint source-document link (when the invoice carries one — the
// schema guarantees it parses, so the hostname is read straight off it), the
// allocated-vs-total header, each charge group (agreed vs paid + an
// exceptions-only price pill), and, under each charge, every PO-line split with
// its AFTER-THIS-INVOICE balance flag and a contention signal when another
// review invoice draws from the same line (near-limit and contention both go
// quiet once the invoice is approved; overdrawn stays). For a `review` invoice it also owns
// the gated actions — Approve & email finance (opens ApproveInvoiceDialog,
// which collects an optional comment and on-behalf-of details, runs the
// approve mutation, and hands the response to `onApproved` for a later preview
// modal), Query — surfacing a 409 optimistic-lock conflict inline (the
// approve dialog renders its own errors inline instead; the row-level conflict
// banner now only serves Query) — and Copy payment table, sourced from the
// invoice's own owning PO (never the possibly-different selected `po`
// fallback), gated on the same balanced check the approve endpoint enforces
// plus a stale-split guard (a split pointing at a PO line the owning PO no
// longer has), with visible copied/failed feedback since clipboard writes can
// silently fail on permission. A `queried` invoice shows a status note
// instead. A `draft` invoice shows guidance to finish the charge breakdown
// plus an Edit action. An `approved` invoice shows the approver name (falling
// back to the stored id for invoices approved before the name snapshot
// existed) — or, when approved on someone else's behalf, that person's name
// and the date their approval was received, with "recorded by" crediting the
// app user who logged it — alongside a comment caption when one was left, a
// "View approval email" button that reopens EmailPreviewModal on the stored
// draft (only when one exists — invoices approved before the feature shipped
// carry none), and a Revert-to-review action that opens RevertInvoiceDialog
// and, on confirm, runs the revert mutation to clear the approval and return
// the invoice to the queue.
//
// Any invoice not yet approved also shows its approval-due date (invoiceDate
// + the overdue threshold) — computed purely from invoiceDate, so it still
// renders in the degraded no-derivation state, which is exactly when the row
// most needs that recovery context.
//
// Line labels resolve against a contract-wide line lookup (`allLinesById`, every
// PO's lines) when provided, so an invoice belonging to a different PO than the
// one selected still shows its real line labels in the contract-wide (All /
// Approved) queue views; it falls back to the passed PO's own lines otherwise. A
// split pointing at a line no PO has (deleted line) falls back to a neutral
// label rather than blanking.
//
// `owningPo` carries the invoice's own PO (resolved by the queue from
// `invoice.poId`), distinct from `po` (the currently-selected PO passed as a
// fallback).

import { useState } from "react";
import {
  AlertTriangle,
  Check,
  Copy,
  ExternalLink,
  Mail,
  MessageSquare,
  Pencil,
  Undo2,
} from "lucide-react";
import Button from "../../common/Button";
import ValidationPill from "./ValidationPill";
import RevertInvoiceDialog from "./RevertInvoiceDialog";
import ApproveInvoiceDialog from "./ApproveInvoiceDialog";
import EmailPreviewModal from "./EmailPreviewModal";
import {
  useApproveInvoice,
  useQueryInvoice,
  useRevertInvoice,
} from "../../../hooks/useFinance";
import { isConflictError } from "../../../services/api";
import {
  approvalEmailSplits,
  buildPaymentTable,
  formatAud,
  invoiceApprovalDueDate,
} from "../../../utils/financeCalc";
import { copyRichText } from "../../../utils/richClipboard";
import { formatDayMonthYear } from "../../../utils/formatShortDate";
import type {
  InvoiceWithDerived,
  PurchaseOrderWithDerived,
  ApproveInvoiceResponse,
  ApproveInvoiceInput,
  PoLine,
} from "../../../types/finance";
import type { DerivedSplit } from "../../../utils/financeCalc";

interface InvoiceRowDetailProps {
  contractId: string;
  invoice: InvoiceWithDerived;
  po: PurchaseOrderWithDerived;
  /** The invoice's own PO resolved by `invoice.poId` (the queue is
   *  contract-wide, so this can differ from the selected `po` fallback).
   *  Undefined when the PO can't be resolved — the copy action then hides. */
  owningPo?: PurchaseOrderWithDerived;
  /** Contract-wide line lookup (every PO's lines) so an invoice from a different
   *  PO than the selected one still resolves its real line labels. Falls back to
   *  the passed PO's own lines when absent. */
  allLinesById?: Map<string, PoLine>;
  /** Handed the approve response (invoice + email preview + warnings) so a
   *  later task can open the email-preview modal. */
  onApproved: (response: ApproveInvoiceResponse) => void;
  /** Opens the record-invoice modal in edit mode for this invoice. */
  onEdit?: () => void;
}

function lineLabel(line: PoLine | undefined): string {
  return line ? `Line ${line.lineNumber} — ${line.description}` : "Unmapped line";
}

// After-this-invoice balance flag. Only the exception rungs (overdrawn / near
// limit) surface — a healthy split carries no flag. Every figure is the
// post-approval remaining, so the copy says so explicitly. On an approved
// invoice the near-limit rung goes quiet (budget headroom is tracked on the
// Budget lines tab once the money is committed) while overdrawn stays — an
// approved overdraw is a data problem worth surfacing.
function splitFlag(
  split: DerivedSplit,
  approved: boolean,
): { className: string; text: string } | null {
  if (split.balance.kind === "over") {
    return {
      className: "text-red-600 dark:text-red-400",
      text: `Overdrawn ${formatAud(-split.remainingAfter)} after this invoice`,
    };
  }
  if (approved) return null;
  if (split.balance.kind === "warn") {
    return {
      className: "text-amber-700 dark:text-amber-400",
      text: `Line near limit · ${formatAud(
        split.remainingAfter,
      )} left after this invoice`,
    };
  }
  return null;
}

// Informational contention tooltip: another review invoice draws from the same
// line. Escalates when approving both would overdraw. Never blocks.
function contentionTitle(split: DerivedSplit): string {
  const base = `Invoice ${split.contendingInvoiceNumbers.join(
    ", ",
  )} (in review) also draws ${formatAud(
    split.pendingElsewhere,
  )} from this line`;
  if (!split.jointOverdraw) return base;
  const overdraw = -(split.remainingAfter - split.pendingElsewhere);
  return `${base} — if both are approved this line overdraws by ${formatAud(
    overdraw,
  )}`;
}

export default function InvoiceRowDetail({
  contractId,
  invoice,
  po,
  owningPo,
  allLinesById,
  onApproved,
  onEdit,
}: InvoiceRowDetailProps) {
  const approve = useApproveInvoice();
  const query = useQueryInvoice();
  const revert = useRevertInvoice();
  const [conflict, setConflict] = useState(false);
  const [revertOpen, setRevertOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [copyState, setCopyState] = useState<"copied" | "failed" | null>(null);

  // Approval deadline — depends only on invoiceDate, so it renders even when
  // allocation detail is unavailable below (that's when the row most needs
  // recovery context).
  const approvalDue =
    invoice.status !== "approved" ? invoiceApprovalDueDate(invoice) : null;
  const approvalDueLine = approvalDue ? (
    <p className="text-caption text-slate-500 dark:text-slate-400">
      Approval due {formatDayMonthYear(approvalDue)}
    </p>
  ) : null;

  const derived = invoice.derived;
  if (!derived) {
    return (
      <div className="space-y-3">
        {approvalDueLine}
        <p className="text-body-sm text-slate-500 dark:text-slate-400">
          Allocation detail is unavailable — the linked purchase order may have
          been removed.
        </p>
      </div>
    );
  }

  const lineById =
    allLinesById ?? new Map<string, PoLine>(po.lines.map((l) => [l.id, l]));

  const lineIds = new Set<string>();
  for (const c of derived.charges) {
    for (const s of c.splits) lineIds.add(s.poLineId);
  }
  const lineCount = lineIds.size;

  const isMutating = approve.isPending || query.isPending || revert.isPending;

  // Copy payment table gates — mirror the approve endpoint (balanced) and
  // fail closed on allocations pointing at removed PO lines: derived.balanced
  // compares totals only, so a stale split would otherwise copy raw line ids.
  const owningLineIds = owningPo ? new Set(owningPo.lines.map((l) => l.id)) : null;
  const staleSplit =
    owningLineIds !== null &&
    invoice.charges.some((c) => c.splits.some((s) => !owningLineIds.has(s.poLineId)));
  const copyDisabledReason = !derived.balanced
    ? "Allocated splits must equal the invoice total"
    : staleSplit
      ? "An allocation references a removed PO line"
      : null;

  const handleCopyPaymentTable = async () => {
    if (!owningPo) return;
    const { text, html } = buildPaymentTable({
      poNumber: owningPo.poNumber,
      totalAmount: invoice.totalAmount,
      splits: approvalEmailSplits(owningPo.lines, invoice.charges),
    });
    try {
      await copyRichText(html, text);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  };

  const handleApproveConfirm = async (input: ApproveInvoiceInput) => {
    const response = await approve.mutateAsync({
      contractId,
      invoiceId: invoice.id,
      input,
    });
    onApproved(response);
  };

  const handleQuery = async () => {
    setConflict(false);
    try {
      await query.mutateAsync({ contractId, invoiceId: invoice.id });
    } catch (err) {
      setConflict(isConflictError(err));
    }
  };

  return (
    <div className="space-y-3">
      {invoice.sharePointUrl && (
        <a
          href={invoice.sharePointUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-body-sm text-brand-600 hover:underline dark:text-brand-400"
        >
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          SharePoint · {new URL(invoice.sharePointUrl).hostname}
        </a>
      )}
      <div
        data-testid="invoice-detail-header"
        className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-body-sm text-slate-700 dark:text-slate-300"
      >
        <span>
          Allocated{" "}
          <span className="font-medium tabular-nums text-slate-900 dark:text-slate-100">
            {formatAud(derived.allocatedTotal)}
          </span>{" "}
          of invoice total{" "}
          <span className="font-medium tabular-nums text-slate-900 dark:text-slate-100">
            {formatAud(invoice.totalAmount)}
          </span>
        </span>
        <span className="text-slate-400 dark:text-slate-500">·</span>
        <span className="text-slate-500 dark:text-slate-400">
          {lineCount} PO {lineCount === 1 ? "line" : "lines"}
        </span>
        {!derived.balanced && (
          <span className="inline-flex items-center gap-1 text-caption font-medium text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            Allocations don&apos;t add up to the invoice total yet
          </span>
        )}
      </div>

      <ul className="space-y-2">
        {derived.charges.map((charge, index) => {
          // deriveInvoice builds derived.charges parallel to invoice.charges
          // (one entry per charge, same order), so the source charge is
          // resolved by index — label + expectedAmount always present.
          const source = invoice.charges[index];
          const chargeSum = charge.splits.reduce((acc, s) => acc + s.amount, 0);
          const expected = source.expectedAmount;
          return (
            <li
              key={charge.chargeId}
              data-testid={`invoice-charge-${charge.chargeId}`}
              className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-body-sm font-medium text-slate-900 dark:text-slate-100">
                  {source.label}
                </span>
                <div className="flex items-center gap-3 text-caption text-slate-500 dark:text-slate-400">
                  {expected !== null && expected !== undefined && (
                    <span>
                      agreed{" "}
                      <span className="tabular-nums">{formatAud(expected)}</span>
                    </span>
                  )}
                  <span>
                    paid{" "}
                    <span className="tabular-nums text-slate-700 dark:text-slate-300">
                      {formatAud(chargeSum)}
                    </span>
                  </span>
                  <ValidationPill kind={charge.price.kind} label={charge.price.text} />
                </div>
              </div>

              <ul className="mt-2 space-y-1">
                {charge.splits.map((split) => {
                  const flag = splitFlag(split, invoice.status === "approved");
                  const line = lineById.get(split.poLineId);
                  return (
                    <li
                      key={split.poLineId}
                      className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-caption"
                    >
                      <span className="text-slate-600 dark:text-slate-400">
                        {lineLabel(line)}
                      </span>
                      <span className="tabular-nums text-slate-700 dark:text-slate-300">
                        {formatAud(split.amount)}
                      </span>
                      {flag && (
                        <span
                          data-testid={`split-flag-${split.poLineId}`}
                          title={`Line ${line?.lineNumber ?? "?"}: ${formatAud(
                            split.remainingBefore,
                          )} before → ${formatAud(split.remainingAfter)} after`}
                          className={`inline-flex items-center gap-1 font-medium ${flag.className}`}
                        >
                          <AlertTriangle
                            className="h-3 w-3 shrink-0"
                            aria-hidden="true"
                          />
                          {flag.text}
                        </span>
                      )}
                      {/* Contention is moot once this invoice is approved —
                          the draw it warned about has already happened. */}
                      {split.pendingElsewhere > 0 && invoice.status !== "approved" && (
                        <span
                          data-testid={`split-contention-${split.poLineId}`}
                          title={contentionTitle(split)}
                          className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400"
                        >
                          <AlertTriangle
                            className="h-3 w-3 shrink-0"
                            aria-hidden="true"
                          />
                          <span className="sr-only">{contentionTitle(split)}</span>
                          <span aria-hidden="true">Also in review</span>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ul>

      {conflict && (
        <p
          role="alert"
          className="text-caption font-medium text-red-600 dark:text-red-400"
        >
          This invoice changed — reload and try again.
        </p>
      )}

      {approvalDueLine}

      {invoice.status === "review" && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            icon={<Check className="h-4 w-4" aria-hidden="true" />}
            onClick={() => setApproveOpen(true)}
            loading={approve.isPending}
            disabled={isMutating}
          >
            Approve &amp; email finance
          </Button>
          <Button
            variant="secondary"
            size="sm"
            icon={<MessageSquare className="h-4 w-4" aria-hidden="true" />}
            onClick={handleQuery}
            loading={query.isPending}
            disabled={isMutating}
          >
            Query
          </Button>
          {onEdit && (
            <Button
              variant="ghost"
              size="sm"
              icon={<Pencil className="h-4 w-4" aria-hidden="true" />}
              onClick={onEdit}
              disabled={isMutating}
            >
              Edit
            </Button>
          )}
          {owningPo && (
            <Button
              variant="secondary"
              size="sm"
              icon={<Copy className="h-4 w-4" aria-hidden="true" />}
              onClick={handleCopyPaymentTable}
              disabled={isMutating || copyDisabledReason !== null}
              title={copyDisabledReason ?? undefined}
            >
              Copy payment table
            </Button>
          )}
          <span aria-live="polite" className="text-caption">
            {copyState === "copied" && (
              <span className="text-emerald-700 dark:text-emerald-400">
                Payment table copied to clipboard
              </span>
            )}
            {copyState === "failed" && (
              <span className="text-red-600 dark:text-red-400">
                Copy failed — check clipboard permissions
              </span>
            )}
          </span>
        </div>
      )}

      {invoice.status === "approved" && (
        <div className="flex flex-wrap items-center gap-2">
          {/* An approved invoice always carries approvedBy + approvedAt (the
              backend stamps both on approval), so both render
              unconditionally — no falsy arm to leave as a dead branch. The
              name is a snapshot taken at approve time; invoices approved
              before it existed fall back to the stored user id. When the
              approval was recorded on someone else's behalf, that person's
              name and the date THEIR approval was received lead instead —
              the app user who logged it is credited as "recorded by". */}
          {invoice.approvedOnBehalf ? (
            <p className="text-caption text-slate-500 dark:text-slate-400">
              Approved by {invoice.approvedOnBehalf.name} ·{" "}
              {formatDayMonthYear(invoice.approvedOnBehalf.receivedAt)} · recorded by{" "}
              {invoice.approvedByName ?? invoice.approvedBy}
            </p>
          ) : (
            <p className="text-caption text-slate-500 dark:text-slate-400">
              Approved by {invoice.approvedByName ?? invoice.approvedBy} ·{" "}
              {formatDayMonthYear(invoice.approvedAt)}
            </p>
          )}
          {invoice.approvalEmail && (
            <Button
              variant="secondary"
              size="sm"
              icon={<Mail className="h-4 w-4" aria-hidden="true" />}
              onClick={() => setEmailOpen(true)}
              disabled={isMutating}
            >
              View approval email
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            icon={<Undo2 className="h-4 w-4" aria-hidden="true" />}
            onClick={() => setRevertOpen(true)}
            disabled={isMutating}
          >
            Revert to review
          </Button>
        </div>
      )}

      {invoice.status === "approved" && invoice.approvalComment && (
        <p className="text-caption text-slate-500 dark:text-slate-400">
          {invoice.approvalComment}
        </p>
      )}

      {invoice.status === "draft" && (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-caption text-slate-500 dark:text-slate-400">
            Draft — add the charge breakdown, then save it to the review queue.
          </p>
          {onEdit && (
            <Button
              variant="secondary"
              size="sm"
              icon={<Pencil className="h-4 w-4" aria-hidden="true" />}
              onClick={onEdit}
            >
              Edit
            </Button>
          )}
        </div>
      )}

      {invoice.status === "queried" && (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-caption text-slate-500 dark:text-slate-400">
            Queried — edit &amp; re-save to return to review.
          </p>
          {onEdit && (
            <Button
              variant="secondary"
              size="sm"
              icon={<Pencil className="h-4 w-4" aria-hidden="true" />}
              onClick={onEdit}
            >
              Edit
            </Button>
          )}
        </div>
      )}

      <RevertInvoiceDialog
        isOpen={revertOpen}
        onClose={() => setRevertOpen(false)}
        invoiceNumber={invoice.invoiceNumber}
        onConfirm={(reason) =>
          revert.mutateAsync({ contractId, invoiceId: invoice.id, reason })
        }
        isSubmitting={revert.isPending}
      />
      <ApproveInvoiceDialog
        isOpen={approveOpen}
        onClose={() => setApproveOpen(false)}
        invoiceNumber={invoice.invoiceNumber}
        totalAmount={invoice.totalAmount}
        gstApplies={invoice.gstApplies}
        onConfirm={handleApproveConfirm}
        isSubmitting={approve.isPending}
      />
      <EmailPreviewModal
        isOpen={emailOpen}
        onClose={() => setEmailOpen(false)}
        invoiceNumber={invoice.invoiceNumber}
        email={invoice.approvalEmail ?? null}
      />
    </div>
  );
}
